/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { serversRuPhrases } from "@/components/servers/i18n";
import { extraRuPhrases, ruPatterns } from "@/utils/language.extra";

export type Language = "en" | "ru";

const STORAGE_KEY = "nyrvex-language";

const translations = {
  en: {
    home: "Home",
    dashboard: "Dashboard",
    speedTest: "Speed Test",
    traceroute: "Traceroute",
    agents: "Agents",
    servers: "Servers",
    network: "Network",
    switches: "Switches",
    routers: "Routers",
    docker: "Docker",
    settings: "Settings",
    language: "Language",
    interfaceLanguage: "Interface language",
    english: "English",
    russian: "Russian",
    security: "Security",
    account: "Account",
    username: "Username",
    password: "Password",
    currentPassword: "Current password",
    newPassword: "New password",
    confirmNewPassword: "Confirm new password",
    saveUsername: "Save username",
    changePassword: "Change password",
    twoFactorAuth: "Two-Factor Authentication",
    loginHistory: "Login History",
    signIn: "Sign in",
    noAdminUser: "No admin user exists yet. Create one on the server before signing in.",
    routersSubtitle: "OpenWrt routers reachable through the VPS SSH tunnel",
    refresh: "Refresh",
    healthy: "Healthy",
    needsAttention: "Needs attention",
    fixYouTube: "Fix YouTube",
    checkAvailability: "Check availability",
    backupRouter: "Backup router",
    restartZapret: "Restart zapret",
    restartDNS: "Restart DNS",
    restartTunnel: "Restart SSH tunnel",
    installDNSWatchdog: "Install DNS watchdog",
    checkDNS: "Check DNS",
    familyRouter: "Family router",
    youtubeWorks: "YouTube works",
    adBlockingWorks: "Ad blocking works",
    remoteAccessActive: "Remote access active",
    enoughMemory: "Enough memory",
    dnsAdblock: "DNS ads",
    dnsInternet: "DNS internet",
    routerActions: "Router actions",
    availabilityCheck: "Availability Check",
    routerFallbackName: "Router",
    routerCheckScope: "SSH, YouTube domains and services in real time",
    waitingForCheck: "Waiting for check...",
    services: "Services",
    checkConnectionLost: "The check connection was interrupted",
    allChecksPassed: "All checks passed successfully",
    someChecksFailed: "Some checks failed, review the rows above",
    checkStillRunning: "Check is still running...",
    running: "Running...",
    hostname: "Hostname",
    model: "Model",
    kernel: "Kernel",
    load: "Load",
    uptime: "Uptime",
    storage: "Storage",
    memory: "Memory",
    openwrt: "OpenWrt",
    ram: "RAM",
    ramNormal: "RAM usage is normal for OpenWrt",
    ramWarning: "RAM is getting tight, but the router is still safe",
    ramCritical: "RAM is critically low, router services may restart",
    overlay: "Overlay",
    used: "used",
    available: "available",
    freeOf: "free of",
    wifi: "Wi-Fi",
    interfaces: "Interfaces",
    name: "Name",
    loadingRouters: "Loading routers...",
    noRouters: "No routers configured",
    infrastructureCenter: "Infrastructure Center",
    infrastructureSubtitle: "Routers, servers, containers and network checks in one place.",
    noCriticalProblems: "No critical problems",
    problems: "Problems",
    sshYoutubeServicesOk: "SSH, YouTube and services are healthy",
    routersNeedAttention: "Some routers need attention",
    monitoringAgentsEnabled: "Monitoring agents are enabled",
    agentsNotConfigured: "Agents are not configured yet",
    dockerHostsMonitored: "Docker hosts are monitored",
    dockerHostsNotConfigured: "Docker hosts are not configured yet",
    snmpDevicesEnabled: "SNMP devices are enabled",
    snmpCanBeAddedLater: "SNMP can be added later",
    routersRemoteAccess: "Routers and remote access",
    routersNotConfigured: "Routers are not configured yet.",
    ok: "OK",
    check: "Check",
    whatToMonitor: "What to monitor here",
    monitorRoutersHint: "Family routers: YouTube, zapret, SSH tunnel, memory.",
    monitorServersHint: "Homelab and VPS: load, disks, network, agents.",
    monitorDockerHint: "Docker: containers, logs, service restarts.",
    monitorNetworkHint: "Network: speedtest, traceroute, packet loss and routes.",
    quickAccess: "Quick Access",
    quickAccessSubtitle: "Links to homelab, VPS and internal panels.",
    edit: "Edit",
    done: "Done",
    addService: "Add Service",
    noServicesYet: "No services yet",
    noServicesHint: "Add links to panels and services running on your VPS or homelab.",
    deleteService: "Delete Service",
    networkSubtitle: "Speedtest, traceroute and route checks for your infrastructure",
    routerHealth: "Router health",
    busy: "busy",
    connections: "connections",
    activeDNS: "Active DNS",
    defaultRoute: "Default route",
    wifiClients: "Wi-Fi clients",
    expected: "Expected",
    inactive: "Inactive",
    latency: "Latency",
    noReply: "No reply",
    recentLogWarnings: "Recent log warnings",
    editProfile: "Edit profile",
    routerProfile: "Router profile",
    routerProfileHint: "Name this router and keep useful field notes for later maintenance.",
    displayName: "Display name",
    location: "Location",
    owner: "Owner",
    purpose: "Purpose",
    notes: "Notes",
    cancel: "Cancel",
    save: "Save",
    routerProfileSaved: "Router profile saved",
    routerProfileSaveFailed: "Failed to save router profile",
    backToRouters: "Back to routers",
    routerDetails: "Router details",
  },
  ru: {
    home: "Главная",
    dashboard: "Обзор",
    speedTest: "Скорость",
    traceroute: "Маршрут",
    agents: "Агенты",
    servers: "Серверы",
    network: "Сеть",
    switches: "Коммутаторы",
    routers: "Роутеры",
    docker: "Docker",
    settings: "Настройки",
    language: "Язык",
    interfaceLanguage: "Язык интерфейса",
    english: "Английский",
    russian: "Русский",
    security: "Безопасность",
    account: "Аккаунт",
    username: "Имя пользователя",
    password: "Пароль",
    currentPassword: "Текущий пароль",
    newPassword: "Новый пароль",
    confirmNewPassword: "Повторите новый пароль",
    saveUsername: "Сохранить имя",
    changePassword: "Сменить пароль",
    twoFactorAuth: "Двухфакторная аутентификация",
    loginHistory: "История входов",
    signIn: "Войти",
    noAdminUser: "Администратор ещё не создан. Создайте его на сервере перед входом.",
    routersSubtitle: "OpenWrt-роутеры, доступные через SSH-туннель на VPS",
    refresh: "Обновить",
    healthy: "Всё хорошо",
    needsAttention: "Нужно внимание",
    fixYouTube: "Починить YouTube",
    checkAvailability: "Проверить доступность",
    backupRouter: "Сделать бэкап",
    restartZapret: "Перезапустить zapret",
    restartDNS: "Перезапустить DNS",
    restartTunnel: "Перезапустить SSH-туннель",
    installDNSWatchdog: "Установить DNS-watchdog",
    checkDNS: "Проверить DNS",
    familyRouter: "Семейный роутер",
    youtubeWorks: "YouTube работает",
    adBlockingWorks: "Реклама блокируется",
    remoteAccessActive: "Удалённый доступ активен",
    enoughMemory: "Памяти достаточно",
    dnsAdblock: "DNS-реклама",
    dnsInternet: "DNS-интернет",
    routerActions: "Действия роутера",
    availabilityCheck: "Проверка доступности",
    routerFallbackName: "Роутер",
    routerCheckScope: "SSH, YouTube-домены и сервисы в реальном времени",
    waitingForCheck: "Ожидание проверки...",
    services: "Сервисы",
    checkConnectionLost: "Соединение с проверкой оборвалось",
    allChecksPassed: "Все проверки прошли успешно",
    someChecksFailed: "Есть проблемы, проверьте строки выше",
    checkStillRunning: "Проверка ещё идёт...",
    running: "Выполняется...",
    hostname: "Имя хоста",
    model: "Модель",
    kernel: "Ядро",
    load: "Нагрузка",
    uptime: "Время работы",
    storage: "Накопитель",
    memory: "Память",
    openwrt: "OpenWrt",
    ram: "ОЗУ",
    ramNormal: "Использование ОЗУ нормально для OpenWrt",
    ramWarning: "ОЗУ становится мало, но роутер ещё в безопасной зоне",
    ramCritical: "ОЗУ критически мало, сервисы роутера могут перезапускаться",
    overlay: "Overlay",
    used: "занято",
    available: "доступно",
    freeOf: "свободно из",
    wifi: "Wi-Fi",
    interfaces: "Интерфейсы",
    name: "Имя",
    loadingRouters: "Загрузка роутеров...",
    noRouters: "Роутеры не настроены",
    infrastructureCenter: "Центр инфраструктуры",
    infrastructureSubtitle: "Роутеры, серверы, контейнеры и сеть в одном месте.",
    noCriticalProblems: "Критичных проблем нет",
    problems: "Проблем",
    sshYoutubeServicesOk: "SSH, YouTube и сервисы в норме",
    routersNeedAttention: "Есть роутеры, требующие внимания",
    monitoringAgentsEnabled: "Агенты мониторинга включены",
    agentsNotConfigured: "Агенты пока не настроены",
    dockerHostsMonitored: "Docker-хосты под наблюдением",
    dockerHostsNotConfigured: "Docker-хосты пока не добавлены",
    snmpDevicesEnabled: "SNMP-устройства включены",
    snmpCanBeAddedLater: "SNMP можно добавить позже",
    routersRemoteAccess: "Роутеры и удалённый доступ",
    routersNotConfigured: "Роутеры ещё не настроены.",
    ok: "ОК",
    check: "Проверить",
    whatToMonitor: "Что здесь контролировать",
    monitorRoutersHint: "Семейные роутеры: YouTube, zapret, SSH-туннель, память.",
    monitorServersHint: "Homelab и VPS: нагрузка, диски, сеть, агенты.",
    monitorDockerHint: "Docker: контейнеры, логи, рестарт сервисов.",
    monitorNetworkHint: "Сеть: speedtest, traceroute, потери и маршруты.",
    quickAccess: "Быстрый доступ",
    quickAccessSubtitle: "Ссылки на сервисы homelab, VPS и внутренние панели.",
    edit: "Изменить",
    done: "Готово",
    addService: "Добавить сервис",
    noServicesYet: "Сервисов пока нет",
    noServicesHint: "Добавьте ссылки на панели и сервисы, которые живут на VPS или в homelab.",
    deleteService: "Удалить сервис",
    networkSubtitle: "Speedtest, traceroute и проверка маршрутов для твоей инфраструктуры",
    routerHealth: "Здоровье роутера",
    busy: "занято",
    connections: "соединений",
    activeDNS: "Активный DNS",
    defaultRoute: "Маршрут по умолчанию",
    wifiClients: "Wi-Fi клиенты",
    expected: "Ожидаемо",
    inactive: "Простой",
    latency: "Задержка",
    noReply: "Нет ответа",
    recentLogWarnings: "Последние предупреждения логов",
    editProfile: "Изменить профиль",
    routerProfile: "Профиль роутера",
    routerProfileHint: "Назови роутер и сохрани полезные заметки для обслуживания.",
    displayName: "Название",
    location: "Место",
    owner: "Для кого",
    purpose: "Назначение",
    notes: "Заметки",
    cancel: "Отмена",
    save: "Сохранить",
    routerProfileSaved: "Профиль роутера сохранён",
    routerProfileSaveFailed: "Не удалось сохранить профиль роутера",
    backToRouters: "Назад к роутерам",
    routerDetails: "Детали роутера",
  },
} as const;

type TranslationKey = keyof typeof translations.en;

type PhraseDictionary = Record<string, string>;

const ruPhrases: PhraseDictionary = {
  ...serversRuPhrases,
  ...extraRuPhrases,
  "Single Trace": "Один маршрут",
  Monitors: "Мониторы",
  Traceroute: "Маршрут",
  "Trace network paths to see which backbone providers you travel through": "Проверь путь сети и провайдеров, через которые идёт трафик",
  "Enter hostname/IP (e.g., google.com, 8.8.8.8)": "Введите хост/IP (например, google.com, 8.8.8.8)",
  Trace: "Проверить",
  Running: "Выполняется",
  "or select from available servers": "или выберите доступный сервер",
  "Search servers...": "Поиск серверов...",
  "All Types": "Все типы",
  "Load More": "Показать ещё",
  "Run a Traceroute": "Запустить трассировку",
  "Discover every hop packets take to reach a destination": "Посмотрите каждый узел на пути пакетов до цели",
  "Quick Start": "Быстрый старт",
  "Enter any hostname or IP address to trace its route, or try these popular destinations:": "Введите любой хост или IP для трассировки либо выберите популярную цель:",
  "Hop-by-Hop Analysis": "Анализ по узлам",
  "See each router in the path": "Посмотрите каждый маршрутизатор на пути",
  "Create Monitor": "Создать монитор",
  "Convert results into a scheduled monitor": "Превратите результат в мониторинг по расписанию",
  "scheduled monitor": "мониторинг по расписанию",
  "Network Monitors": "Сетевые мониторы",
  "Continuous monitoring with MTR or ICMP ping": "Постоянный мониторинг через MTR или ICMP ping",
  Add: "Добавить",
  Edit: "Изменить",
  Delete: "Удалить",
  Cancel: "Отмена",
  Save: "Сохранить",
  Update: "Обновить",
  Create: "Создать",
  Done: "Готово",
  Status: "Статус",
  Overview: "Обзор",
  Dashboard: "Обзор",
  Settings: "Настройки",
  Home: "Главная",
  "Speed Test": "Скорость",
  Speed: "Скорость",
  Agents: "Агенты",
  Servers: "Серверы",
  Network: "Сеть",
  Switches: "Коммутаторы",
  Routers: "Роутеры",
  "Server Overview": "Обзор серверов",
  "Add Service": "Добавить сервис",
  "Delete Service": "Удалить сервис",
  "This action cannot be undone.": "Это действие нельзя отменить.",
  "Docker host added": "Docker-хост добавлен",
  "Docker host updated": "Docker-хост обновлён",
  "Docker host deleted": "Docker-хост удалён",
  "Containers running on monitored Docker hosts": "Контейнеры на отслеживаемых Docker-хостах",
  "Add Docker Host": "Добавить Docker-хост",
  "Docker Hosts": "Docker-хосты",
  "No Docker hosts configured": "Docker-хосты не настроены",
  "Add a host (usually the local socket) to start monitoring containers": "Добавьте хост, обычно локальный socket, чтобы начать мониторинг контейнеров",
  Containers: "Контейнеры",
  "Edit Docker Host": "Изменить Docker-хост",
  "Home Server": "Домашний сервер",
  "Docker Host": "Docker-хост",
  Start: "Запустить",
  Stop: "Остановить",
  Restart: "Перезапустить",
  CPU: "CPU",
  Memory: "Память",
  Storage: "Накопитель",
  Logs: "Логи",
  "No containers": "Контейнеров нет",
  "Monitor created successfully": "Монитор создан",
  "Failed to create monitor": "Не удалось создать монитор",
  "Monitor deleted successfully": "Монитор удалён",
  "Failed to delete monitor": "Не удалось удалить монитор",
  "Monitor stopped": "Монитор остановлен",
  "Failed to stop monitor": "Не удалось остановить монитор",
  "Unknown error occurred": "Произошла неизвестная ошибка",
  "Monitor has been removed": "Монитор удалён",
  "Monitor is now inactive": "Монитор отключён",
  "Traceroute started": "Трассировка запущена",
  "Traceroute completed": "Трассировка завершена",
  "Traceroute failed": "Трассировка не удалась",
  "Traceroute failed. Please check the hostname and try again.": "Трассировка не удалась. Проверьте хост и попробуйте снова.",
  "Traceroute Results": "Результаты трассировки",
  "Create packet loss monitor for this host": "Создать монитор потерь для этого хоста",
  "Packet Loss": "Потери пакетов",
  "Packet Loss Monitor": "Монитор потерь",
  "No monitors configured": "Мониторы не настроены",
  Search: "Поиск",
  Enabled: "Включено",
  Disabled: "Отключено",
  Online: "Онлайн",
  Offline: "Оффлайн",
  Healthy: "Всё хорошо",
  "Needs attention": "Нужно внимание",
  Refresh: "Обновить",
  "Fix YouTube": "Починить YouTube",
  "Check availability": "Проверить доступность",
  Hostname: "Имя хоста",
  Model: "Модель",
  Kernel: "Ядро",
  Load: "Нагрузка",
  Uptime: "Время работы",
  Interfaces: "Интерфейсы",
  Name: "Имя",
  Username: "Имя пользователя",
  Password: "Пароль",
  "Current password": "Текущий пароль",
  "New password": "Новый пароль",
  "Confirm new password": "Повторите новый пароль",
  "Save username": "Сохранить имя",
  "Change password": "Сменить пароль",
  Security: "Безопасность",
  Language: "Язык",
  "Interface language": "Язык интерфейса",
  English: "Английский",
  Russian: "Русский",
  "Sign in": "Войти",
  Login: "Войти",
  "Enter your username": "Введите имя пользователя",
  "Enter your password": "Введите пароль",
  "Time & Date Settings": "Настройки времени и даты",
  "Save Changes": "Сохранить изменения",
  "Dashboard Settings": "Настройки обзора",
  "Wallpaper Settings": "Обои",
  "Notification Settings": "Уведомления",
  "Distance Settings": "Единицы расстояния",
  "Kiosk Settings": "Настройки киоска",
  "Login History": "История входов",
  "Two-Factor Authentication": "Двухфакторная аутентификация",
  "Enable 2FA": "Включить 2FA",
};

const enPhrases: PhraseDictionary = Object.fromEntries(
  Object.entries(ruPhrases).map(([en, ru]) => [ru, en]),
);

const phraseTranslations: Record<Language, PhraseDictionary> = {
  en: enPhrases,
  ru: ruPhrases,
};

interface LanguageContextValue {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: TranslationKey) => string;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

function translatePhrase(value: string, language: Language) {
  const trimmed = value.trim();
  if (!trimmed) return value;

  let translated = phraseTranslations[language][trimmed];
  if (!translated && language === "ru") {
    for (const [re, fn] of ruPatterns) {
      const m = trimmed.match(re);
      if (m) {
        translated = fn(m);
        break;
      }
    }
  }
  if (!translated) return value;

  return value.replace(trimmed, translated);
}

function translateDom(language: Language) {
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;
      if (!parent || ["SCRIPT", "STYLE", "TEXTAREA"].includes(parent.tagName)) {
        return NodeFilter.FILTER_REJECT;
      }
      if (parent.closest("[data-no-translate]")) {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  const textNodes: Text[] = [];
  while (walker.nextNode()) {
    textNodes.push(walker.currentNode as Text);
  }

  textNodes.forEach((node) => {
    const current = node.nodeValue || "";
    const next = translatePhrase(current, language);
    if (next !== current) {
      node.nodeValue = next;
    }
  });

  document.querySelectorAll<HTMLElement>("[placeholder],[title],[aria-label]").forEach((element) => {
    if (element.closest("[data-no-translate]")) return;
    ["placeholder", "title", "aria-label"].forEach((attribute) => {
      const value = element.getAttribute(attribute);
      if (value) {
        const next = translatePhrase(value, language);
        if (next !== value) {
          element.setAttribute(attribute, next);
        }
      }
    });
  });
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === "ru" || saved === "en" ? saved : "en";
  });

  const setLanguage = (nextLanguage: Language) => {
    localStorage.setItem(STORAGE_KEY, nextLanguage);
    setLanguageState(nextLanguage);
  };

  useEffect(() => {
    document.documentElement.lang = language;

    const run = () => requestAnimationFrame(() => translateDom(language));
    run();

    const observer = new MutationObserver(run);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["placeholder", "title", "aria-label"],
    });

    return () => observer.disconnect();
  }, [language]);

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t: (key: TranslationKey) => translations[language][key] || translations.en[key],
    }),
    [language],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
}
